import { useMyLearningPaths } from '@/hooks/queries/learning-paths';
import { useOrderedDocuments } from '@/hooks/queries/ordered-documents';
import PromptService from '@/services/prompt-service';
import { PromptData, StandardResponse } from '@/types/agent';
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Input, Spinner, Tab, Tabs } from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useCreateOrUpdatePractice } from 'Src/hooks/queries/practices';
import { useAuthStore } from 'Src/store/use-auth-store';
import { getGradientForIndexAsString } from '../../utils/colors';
import DiscoverCard from '../discover/components/discover-card';

const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'scenarios' | 'library'>(
    'all'
  );
  const orgId = useAuthStore((state) => state.orgId);
  const createPracticeMutation = useCreateOrUpdatePractice();
  const navigate = useNavigate();
  const [loadingPracticeId, setLoadingPracticeId] = useState<number | null>(
    null
  );

  // Fetch prompts
  const { data: prompts, isLoading: isLoadingPrompts } = useQuery<
    StandardResponse<PromptData>
  >({
    queryKey: ['search-prompts', { is_active: true }],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
          is_active: true,
        },
      });
      return response as StandardResponse<PromptData>;
    },
  });

  // Fetch learning paths
  const { data: learningPaths, isLoading: isLoadingLearningPaths } =
    useMyLearningPaths({});

  // Fetch documents
  const { data: documents, isLoading: isLoadingDocuments } =
    useOrderedDocuments();

  const isLoading = isLoadingPrompts || isLoadingLearningPaths || isLoadingDocuments;

  // Process scenarios
  const scenarios = useMemo(() => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      if (!learningPaths?.results) return [];
      return learningPaths.results.flatMap((path) =>
        (path.prompts || []).map((p) => ({
          id: p.prompt.id,
          name: p.prompt.name,
          description: p.prompt.system_prompt,
          tag: path.tag,
        }))
      );
    }
    return (prompts?.results || []).map((p, index) => ({
      id: p.id,
      name: p.name,
      description: p.system_prompt,
      tag: ['Counselor', 'Action', 'Coach'][index % 3],
    }));
  }, [prompts, learningPaths]);

  // Filter results based on search query
  const filteredScenarios = useMemo(() => {
    if (!searchQuery) return scenarios;
    return scenarios.filter(
      (s) =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [scenarios, searchQuery]);

  const filteredDocuments = useMemo(() => {
    if (!documents?.results) return [];
    if (!searchQuery) return documents.results;
    return documents.results.filter((d) =>
      d.document.filename.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [documents, searchQuery]);

  const handleScenarioClick = async (task: {
    id: number;
    name: string;
    description: string;
    tag?: string;
  }) => {
    try {
      setLoadingPracticeId(task.id);
      const response = (await createPracticeMutation.mutateAsync({
        name: task.name,
        org_id: orgId,
      })) as { id: number };

      const tag = task.tag || 'Counselor';
      navigate(
        `../home/${response.id}/run/?action=${encodeURIComponent(task.name)}&id=${response.id}&tag=${encodeURIComponent(tag)}`
      );
    } catch (error) {
      toast.error('Failed to create practice session');
      console.error(error);
    } finally {
      setLoadingPracticeId(null);
    }
  };

  const showScenarios = activeTab === 'all' || activeTab === 'scenarios';
  const showLibrary = activeTab === 'all' || activeTab === 'library';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col gap-6 pb-24 pt-2"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Search</h1>

        {/* Search Input */}
        <Input
          placeholder="Search scenarios, documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startContent={
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
          }
          classNames={{
            inputWrapper:
              'bg-gray-50 border-1 border-gray-200 hover:border-gray-300 focus-within:!border-primary rounded-xl h-12',
            input: 'text-base',
          }}
          autoFocus
        />
      </motion.div>

      {/* Filter Tabs */}
      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(key) => setActiveTab(key as typeof activeTab)}
        variant="underlined"
        classNames={{
          tabList: 'gap-6',
          tab: 'px-0 h-10',
          tabContent: 'text-gray-500 group-data-[selected=true]:text-primary',
          cursor: 'bg-primary',
        }}
      >
        <Tab key="all" title="All" />
        <Tab key="scenarios" title="Scenarios" />
        <Tab key="library" title="Library" />
      </Tabs>

      {isLoading ? (
        <div className="flex justify-center items-center h-[40vh]">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Scenarios Section */}
          {showScenarios && filteredScenarios.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <SparklesIcon className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Scenarios
                </h2>
                <span className="text-sm text-gray-400">
                  ({filteredScenarios.length})
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {filteredScenarios.slice(0, 6).map((scenario, index) => {
                  const bgColor = getGradientForIndexAsString(
                    scenario.tag,
                    index,
                    true,
                    0.75
                  );
                  const hoverBgColor = getGradientForIndexAsString(
                    scenario.tag,
                    index,
                    true,
                    0.85
                  );
                  return (
                    <DiscoverCard
                      key={scenario.id}
                      title={scenario.name}
                      description={scenario.description}
                      bgColor={bgColor}
                      hoverBgColor={hoverBgColor}
                      onAction={() => handleScenarioClick(scenario)}
                      isLoading={loadingPracticeId === scenario.id}
                    />
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Library Section */}
          {showLibrary && filteredDocuments.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <BookOpenIcon className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Library</h2>
                <span className="text-sm text-gray-400">
                  ({filteredDocuments.length})
                </span>
              </div>
              <div className="space-y-3">
                {filteredDocuments.slice(0, 5).map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                      <BookOpenIcon className="w-5 h-5 text-rose-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {doc.document.filename.replace(/\.[^/.]+$/, '')}
                      </p>
                      <p className="text-xs text-gray-400">Document</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* No Results */}
          {searchQuery &&
            filteredScenarios.length === 0 &&
            filteredDocuments.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-20 text-center"
              >
                <MagnifyingGlassIcon className="w-12 h-12 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">
                  No results found
                </h3>
                <p className="text-gray-400 text-sm">
                  Try searching with different keywords
                </p>
              </motion.div>
            )}
        </div>
      )}
    </motion.div>
  );
};

export default SearchPage;

